import type { IANATimezones } from '@gcv/shared';
import * as luxon from 'luxon';

import { DateTimeHelpers } from './dateTime.util';

/**
 * Return function to validate given date is not in the past.
 *
 * @param timezone client timezone used to construct today's date.
 * @returns validator function.
 */
export const createPastDateValidator = (timezone: IANATimezones) => {
  const validateDate = (value: string | number) => {
    const getStartOfDay = () => {
      return DateTimeHelpers.getStartOfTodayIsoString(timezone);
    };

    const valid = getStartOfDay() <= value.toString();
    return valid || 'cannot be past date.';
  };

  return validateDate;
};

/**
 * Checks if a date string is in the valid format YYYY-MM-DD.
 *
 * @param date The date string to validate.
 * @returns True if valid, false otherwise.
 */
export const isValidYYYYMMDDDate = (date: string): boolean => {
  const isValid = luxon.DateTime.fromFormat(date, 'yyyy-MM-dd').isValid;
  return isValid;
};
/**
 * Checks if a UTC date string is valid using luxon.
 *
 * @param date The UTC date string to validate.
 * @returns True if valid, false otherwise.
 */
export const isValidUTCDate = (date: string): boolean => {
  const dt = luxon.DateTime.fromISO(date, { zone: 'utc' });
  return dt.isValid;
};

/**
 * Checks if a phone number is in the valid format (000) 000-0000.
 *
 * @param phone The phone number string to validate.
 * @returns True if valid, false otherwise.
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  const regex = /^\(\d{3}\) \d{3}-\d{4}$/;
  return regex.test(phone);
};

/**
 * Checks if an EIN (Employer Identification Number) is in the valid format 00-0000000.
 *
 * @param ein The EIN string to validate.
 * @returns True if valid, false otherwise.
 */
export const isValidEIN = (ein: string): boolean => {
  const regex = /^\d{2}-\d{7}$/;
  return regex.test(ein);
};

/**
 * Checks if a zip code is in the valid format 00000 (US) or a0a 0a0 (Canadian).
 *
 * @param zip The zip code string to validate.
 * @returns True if valid, false otherwise.
 */
export const isValidZipCode = (zip: string): boolean => {
  const usZipRegex = /^\d{5}$/;
  const caZipRegex = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;
  return usZipRegex.test(zip) || caZipRegex.test(zip);
};

/**
 * Checks if a Social Security Number (SSN) is in the valid format 000-00-0000.
 *
 * @param ssn The SSN string to validate.
 * @returns True if valid, false otherwise.
 */
export const isValidSSN = (ssn: string): boolean => {
  const regex = /^\d{3}-\d{2}-\d{4}$/;
  return regex.test(ssn);
};
